import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowUpNarrowWide, MonitorPlay, TrendingUp } from "lucide-react";

interface FeatureBoxProps {
    title: string;
    description: string;
    icon?: string;
}

interface AdditionalBenefitsProps {
    title?: string;
    subtitle?: string;
    features?: FeatureBoxProps[];
    buttonText?: string;
    buttonLink?: string;
    className?: string;
    backgroundColor?: string;
}

const DEFAULT_TITLE = "Why Us";
const DEFAULT_SUBTITLE = "Stand out. Impress your guests. Modernise your property. Let’s make your in-room experience effortless, secure, and unforgettable.";
const DEFAULT_FEATURES: FeatureBoxProps[] = [
    {
        title: "Boost Revenue",
        description: "Unlock new opportunities with data insights into guest preferences—spot trends, upsell smartly, and keep your property one step ahead.",
        icon: "TrendingUp"
    },
    {
        title: "Upgrade with Ease",
        description: "Refresh your in-room tech without a major investment. Our casting solutions are affordable, quick to implement, and instantly modernise your offering.",
        icon: "ArrowUpNarrowWide"
    },
    {
        title: "Put Your Brand in the Spotlight",
        description: "Customise the casting interface to match your look and feel—reinforcing your brand at every guest touchpoint.",
        icon: "MonitorPlay"
    }
];
const DEFAULT_BUTTON_TEXT = "Contact Us";
const DEFAULT_BUTTON_LINK = "/contact-us/#contact-us-form";
const DEFAULT_BACKGROUND_COLOR = "bg-white";

export default function AdditionalBenefits({
    title = DEFAULT_TITLE,
    subtitle = DEFAULT_SUBTITLE,
    features = DEFAULT_FEATURES,
    buttonText = DEFAULT_BUTTON_TEXT,
    buttonLink = DEFAULT_BUTTON_LINK,
    className = "",
    backgroundColor = DEFAULT_BACKGROUND_COLOR
}: AdditionalBenefitsProps) {
    return (
        <section className={`${backgroundColor} text-gray-600 ${className} relative overflow-hidden`}>
            {/* Animated Background Elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-harvey-red/10 rounded-full animate-pulse"></div>
                <div className="absolute top-3/4 right-1/4 w-24 h-24 bg-harvey-red/5 rounded-full animate-bounce delay-1000"></div>
                <div className="absolute bottom-1/4 left-1/3 w-16 h-16 bg-harvey-red/15 rounded-full animate-ping delay-2000"></div>
            </div>

            <div className="py-16 xl:px-0 px-4 text-gray-600 flex flex-col items-center relative z-10">
                {/* Enhanced Title with Two-Tone Effect */}
                <div className="text-center mb-8 animate-fade-in-up">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-center pb-4 drop-shadow-2xl">
                        <span className="text-black">More Than Just </span>
                        <span className="text-harvey-red">Entertainment</span>
                    </h1>
                    <div className="w-24 h-1 bg-harvey-red mx-auto rounded-full animate-fade-in-up delay-500"></div>
                </div>

                {/* Enhanced Subtitle */}
                <p className="text-center mb-12 text-xl md:text-2xl font-light text-gray-600 max-w-3xl leading-relaxed animate-fade-in-up delay-700">
                    {subtitle}
                </p>

                {/* Enhanced Feature Grid with Staggered Animations */}
                <div className="grid grid-cols-1 md:grid-cols-3 mx-auto px-2 gap-8 lg:gap-10 max-w-7xl lg:px-20 mt-8">
                    {features.map((feature, index) => {
                        // Icon mapping
                        const getIcon = (iconName?: string) => {
                            switch (iconName) {
                                case 'TrendingUp': return <TrendingUp className="w-8 h-8" />;
                                case 'ArrowUpNarrowWide': return <ArrowUpNarrowWide className="w-8 h-8" />;
                                case 'MonitorPlay': return <MonitorPlay className="w-8 h-8" />;
                                default: return <TrendingUp className="w-8 h-8" />;
                            }
                        };

                        return (
                            <div
                                key={index}
                                className={`animate-fade-in-up delay-${(index + 1) * 300 + 900} h-full`}
                            >
                                <Card className="  border border-gray-300 shadow-lg bg-gray-100 text-gray-800 hover:bg-gray-50 hover:border-gray-500 hover:shadow-2xl hover:shadow-gray-400/20 transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 group h-full flex flex-col">
                                    <CardHeader className="relative pb-4  flex flex-col items-center">
                                        {/* Icon Badge */}
                                        <div className="absolute top-[26px] -left-6 w-12 h-12 bg-harvey-red border-2 border-white rounded-full flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:bg-gray-800 group-hover:text-white transition-all duration-300">
                                            {getIcon(feature.icon)}
                                        </div>
                                        <CardTitle className="w-full text-xl text-center mt-8 font-semibold text-gray-800 group-hover:text-harvey-red transition-colors duration-300">
                                            {feature.title}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="flex-grow pt-2">
                                        <p className="text-gray-700 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                                            {feature.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>
                        );
                    })}
                </div>

            </div>
        </section>
    );
}

export type { AdditionalBenefitsProps, FeatureBoxProps };